// placeProvider — the one write path for putting a person into a room from the
// control panel.
//
// THE TWO RULES THIS FILE EXISTS TO ENFORCE
//
// 1. THE ROW AND THE ROOM DOC MOVE TOGETHER. `RoomCard` resolves
//    `asg.ahpName || room.providerName`, so writing only the assignment row
//    leaves the card showing whoever was there before — a room that reads as
//    staffed and is not. This has already been fixed once in
//    TimelineCaseModal; it is enforced here so the panel cannot repeat it.
//
// 2. A DISPLACED PERSON IS NEVER LEFT NOWHERE. Taking over an occupied room
//    requires the caller to have already decided where the displaced person
//    goes. The existing BenchAssignFlow writes the takeover FIRST and asks
//    afterwards, so closing the modal at that moment strands them: off a room,
//    not on the bench, not marked departed, and nobody prompted about them.
//    Here the decision is an argument, not a follow-up screen.
//
// Ordering note: the displacement is resolved AFTER the seat is filled, so a
// failure part-way leaves the room staffed rather than empty. Given no
// transactions across these collections, "fails toward a staffed room" is the
// only safe direction to fail in.

import {
  updateAssignmentRow,
  setRoomProvider,
  setRosterMemberDeparted,
  createTransfer,
} from "../../firebase/firestore";
import { isValidDisplacedChoice } from "../../domain/panelAssign";

// Clearing a seat and filling it are the same write with different values, so
// they share one patch builder — that is what keeps a clear from forgetting a
// field the fill sets.
export function seatPatch(person) {
  return person
    ? {
        ahpId: person.id,
        ahpName: person.name || "",
        ahpShiftCode: person.shiftCode ?? null,
        // The incoming person's own shift travels with them; the outgoing
        // person's stored departure must not be inherited by whoever replaces
        // them, or the board will show a departure nobody agreed to.
        ahpDepartTime: "",
      }
    : { ahpId: null, ahpName: "", ahpShiftCode: null, ahpDepartTime: "" };
}

export function roomPatch(person) {
  return person
    ? { providerId: person.id, providerName: person.name || "" }
    : { providerId: null, providerName: "" };
}

/**
 * Place `person` into `lane`, displacing whoever is there.
 *
 * @param {object}  opts
 * @param {object}  opts.person        the person being placed, or null to clear the seat
 * @param {object}  opts.lane          the destination lane (roomId + roomName)
 * @param {object}  opts.targetCase    the assignment row to write (from targetCaseFor)
 * @param {object=} opts.displaced     { id, name, subRole } when the room was occupied
 * @param {string=} opts.disposition   "bench" | "home" | "transfer" — REQUIRED when displaced
 * @param {string=} opts.travelDest    destination facilityId, required for "transfer"
 * @param {string}  opts.facilityId
 * @param {string}  opts.dateKey
 * @param {string}  opts.actorUid
 * @param {string=} opts.travelDestName
 */
export async function placeProvider({
  person,
  lane,
  targetCase,
  displaced = null,
  disposition = null,
  travelDest = "",
  travelDestName = "",
  facilityId,
  dateKey,
  actorUid,
}) {
  if (!lane || !lane.roomId) throw new Error("No destination room.");
  // The panel changes PLACEMENT, never membership: it cannot invent a case row
  // for a room that has none, and says so rather than creating schedule data.
  if (!targetCase || !targetCase.id) {
    throw new Error(`${lane.roomName || "That room"} has no case to staff.`);
  }
  if (displaced && !isValidDisplacedChoice(disposition, { travelDest })) {
    throw new Error("Choose where the displaced person goes first.");
  }

  await updateAssignmentRow(targetCase.id, seatPatch(person));
  await setRoomProvider(lane.roomId, roomPatch(person));

  if (!displaced || !displaced.id) return { displacedResolved: null };

  switch (disposition) {
    case "home":
      await setRosterMemberDeparted(facilityId, dateKey, displaced.id, true, actorUid);
      return { displacedResolved: "home" };

    case "transfer":
      // Both writes, in this order: the trip must exist before the person is
      // taken off this bench, or a failure between them loses them entirely.
      await createTransfer({
        providerId: displaced.id,
        providerName: displaced.name || "",
        subRole: displaced.subRole || "crna",
        fromFacilityId: facilityId,
        toFacilityId: travelDest,
        toFacilityName: travelDestName,
        date: dateKey,
        createdBy: actorUid,
      });
      await setRosterMemberDeparted(facilityId, dateKey, displaced.id, true, actorUid);
      return { displacedResolved: "transfer" };

    case "bench":
    default:
      // Nothing to write. Clearing the seat IS returning them to the bench —
      // the bench is derived as "rostered, not departed, not in a room", so an
      // extra write here would be a second source of truth for the same fact.
      return { displacedResolved: "bench" };
  }
}

export default placeProvider;
